/**
 * Per-platform publish options — composer state ⇄ `posts.metadata` keys.
 *
 * Mirror of the server contract in
 * `backend/servers/social/services/publish_options.py` (lane A1, 2026-10-04):
 * the server re-validates, normalizes and preflights every key, so this file
 * only (1) turns the composer's option controls into the metadata payload and
 * (2) surfaces the same problems early as pre-publish-check issues.
 *
 * Keys: `sponsored`, `facebook_format` (Page Story, 2026-10-07), `ig_sponsor_ids`, `instagram_format`, `instagram_trial_reel`,
 * `instagram_trial_graduation`, `instagram_collaborators`, `instagram_audio`
 * (Instagram audio attach — a track from Instagram's own Sound Collection /
 * original sounds, mixed in by Instagram at publish time, NEVER baked into
 * our rendered file; Reels only — feed format + exactly one video, 2026-10-07),
 * `threads_topic_tag`,
 * `threads_poll_options`, `threads_reply_control`, `tiktok_privacy_level`,
 * `tiktok_disable_comment|duet|stitch`, `tiktok_brand_content`,
 * `tiktok_brand_organic`, `gbp_topic_type`, `gbp_event`, `gbp_offer`, `gbp_cta_type`,
 * `youtube_privacy_status`, `youtube_title`, `youtube_made_for_kids` (lane P2, 2026-10-04).
 */
import type { Platform } from "../types/social";
import type { ComposerIssue } from "./platformRules";

export type InstagramFormat = "feed" | "story";
export type FacebookFormat = "feed" | "story";
export type TrialGraduation = "MANUAL" | "SS_PERFORMANCE";
export type ThreadsReplyControl = "" | "everyone" | "accounts_you_follow" | "mentioned_only" | "followers_only";
export type TikTokPrivacy = "" | "PUBLIC_TO_EVERYONE" | "MUTUAL_FOLLOW_FRIENDS" | "FOLLOWER_OF_CREATOR" | "SELF_ONLY";
export type GbpTopicType = "STANDARD" | "EVENT" | "OFFER";
export type GbpCtaType = "" | "BOOK" | "ORDER" | "SHOP" | "LEARN_MORE" | "SIGN_UP" | "CALL";
export type YoutubePrivacyStatus = "" | "public" | "unlisted" | "private";
/** Tri-state so "not yet chosen" is distinguishable from an explicit "no" (YouTube requires an explicit choice). */
export type YoutubeMadeForKids = "" | "yes" | "no";

/** Mirrors `api/instagramAudio.ts` `InstagramAudioType` — kept local so `lib/` never imports `api/` (layer boundary). */
export type InstagramAudioType = "music" | "original_sound";

/** A picked Instagram audio track (Reels only) — mixed in by Instagram at publish time, never baked into our file. */
export interface InstagramAudioSelection {
  /** Instagram audio id (digits). */
  audioId: string;
  audioType: InstagramAudioType;
  /** Shown in the composer without a lookup (edit mode re-opens with these intact). */
  title: string;
  artist: string;
  /** 0..1 — how loud the attached track plays. */
  audioVolume: number;
  /** 0..1 — how loud the video's own sound/voice plays under it. */
  videoVolume: number;
  /** The render already has baked-in music AND the user explicitly chose to mix both anyway. */
  mixOverBakedMusic: boolean;
}

/** Defaults when a track is first picked — the attached track sits under the video's own sound. */
export const DEFAULT_INSTAGRAM_AUDIO_VOLUME = 0.5;
export const DEFAULT_INSTAGRAM_VIDEO_VOLUME = 1;

export const THREADS_TOPIC_TAG_MAX = 50;
export const THREADS_POLL_OPTION_MAX = 25;
export const IG_COLLABORATORS_MAX = 3;
export const IG_SPONSOR_IDS_MAX = 2;
export const GBP_EVENT_TITLE_MAX = 58;
export const YOUTUBE_TITLE_MAX = 100;

export const YOUTUBE_PRIVACY_LABELS: Record<Exclude<YoutubePrivacyStatus, "">, string> = {
  public: "Public",
  unlisted: "Unlisted",
  private: "Private",
};

export const TIKTOK_PRIVACY_LABELS: Record<Exclude<TikTokPrivacy, "">, string> = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends (mutual followers)",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only me",
};

export const THREADS_REPLY_LABELS: Record<Exclude<ThreadsReplyControl, "">, string> = {
  everyone: "Everyone",
  accounts_you_follow: "Profiles you follow",
  mentioned_only: "Only profiles you mention",
  followers_only: "Your followers",
};

export const GBP_CTA_LABELS: Record<Exclude<GbpCtaType, "">, string> = {
  BOOK: "Book",
  ORDER: "Order online",
  SHOP: "Buy",
  LEARN_MORE: "Learn more",
  SIGN_UP: "Sign up",
  CALL: "Call now",
};

export interface PlatformOptionsState {
  /** Paid partnership / sponsored — drives the FTC gate + every network's paid-partnership label. */
  sponsored: boolean;
  igSponsorIds: string;
  /** "story" publishes a Facebook Page Story (one image/video, no caption, no link). */
  facebookFormat: FacebookFormat;
  instagramFormat: InstagramFormat;
  instagramTrialReel: boolean;
  instagramTrialGraduation: TrialGraduation;
  /** Comma / space separated usernames. */
  instagramCollaborators: string;
  /** Instagram audio attach (Reels only) — null = no track picked. */
  instagramAudio: InstagramAudioSelection | null;
  threadsTopicTag: string;
  threadsPollEnabled: boolean;
  threadsPollOptions: string[];
  threadsReplyControl: ThreadsReplyControl;
  tiktokPrivacy: TikTokPrivacy;
  tiktokAllowComment: boolean;
  tiktokAllowDuet: boolean;
  tiktokAllowStitch: boolean;
  tiktokDisclose: boolean;
  tiktokYourBrand: boolean;
  tiktokBrandedContent: boolean;
  gbpTopicType: GbpTopicType;
  gbpEventTitle: string;
  gbpStartDate: string;
  gbpStartTime: string;
  gbpEndDate: string;
  gbpEndTime: string;
  gbpCouponCode: string;
  gbpRedeemUrl: string;
  gbpTerms: string;
  gbpCtaType: GbpCtaType;
  youtubePrivacyStatus: YoutubePrivacyStatus;
  youtubeTitle: string;
  /** No default — YouTube's Data API rejects an upload with no COPPA self-certification. */
  youtubeMadeForKids: YoutubeMadeForKids;
}

/** TikTok guidelines: no default privacy, interactions + disclosure OFF by default. */
export const DEFAULT_PLATFORM_OPTIONS: PlatformOptionsState = {
  sponsored: false,
  igSponsorIds: "",
  facebookFormat: "feed",
  instagramFormat: "feed",
  instagramTrialReel: false,
  instagramTrialGraduation: "MANUAL",
  instagramCollaborators: "",
  instagramAudio: null,
  threadsTopicTag: "",
  threadsPollEnabled: false,
  threadsPollOptions: ["", ""],
  threadsReplyControl: "",
  tiktokPrivacy: "",
  tiktokAllowComment: false,
  tiktokAllowDuet: false,
  tiktokAllowStitch: false,
  tiktokDisclose: false,
  tiktokYourBrand: false,
  tiktokBrandedContent: false,
  gbpTopicType: "STANDARD",
  gbpEventTitle: "",
  gbpStartDate: "",
  gbpStartTime: "",
  gbpEndDate: "",
  gbpEndTime: "",
  gbpCouponCode: "",
  gbpRedeemUrl: "",
  gbpTerms: "",
  gbpCtaType: "",
  youtubePrivacyStatus: "",
  youtubeTitle: "",
  youtubeMadeForKids: "",
};

export interface PlatformOptionsContext {
  platforms: Platform[];
  /** Effective TikTok mode for this post (composer choice). */
  tiktokMode: "direct" | "draft";
  mediaCount: number;
  singleVideo: boolean;
  linkUrl: string;
  /** Does the attached render already have a licensed music bed baked in? `null`/`undefined` = unknown or no render attached. */
  bakedMusic?: boolean | null;
}

export interface PlatformOptionsMetadata {
  sponsored?: true;
  facebook_format?: "story";
  ig_sponsor_ids?: string[];
  instagram_format?: "story";
  instagram_trial_reel?: true;
  instagram_trial_graduation?: TrialGraduation;
  instagram_collaborators?: string[];
  instagram_audio?: {
    audio_id: string;
    audio_volume: number;
    video_volume: number;
    audio_type?: InstagramAudioType;
    title?: string;
    artist?: string;
    mix_over_baked_music?: true;
  };
  threads_topic_tag?: string;
  threads_poll_options?: string[];
  threads_reply_control?: Exclude<ThreadsReplyControl, "">;
  tiktok_privacy_level?: Exclude<TikTokPrivacy, "">;
  tiktok_disable_comment?: boolean;
  tiktok_disable_duet?: boolean;
  tiktok_disable_stitch?: boolean;
  tiktok_brand_content?: boolean;
  tiktok_brand_organic?: boolean;
  gbp_topic_type?: Exclude<GbpTopicType, "STANDARD">;
  gbp_event?: { title: string; start_date: string; start_time?: string; end_date: string; end_time?: string };
  gbp_offer?: { coupon_code?: string; redeem_url?: string; terms?: string };
  gbp_cta_type?: Exclude<GbpCtaType, "">;
  youtube_privacy_status?: Exclude<YoutubePrivacyStatus, "">;
  youtube_title?: string;
  youtube_made_for_kids?: boolean;
}

const IG_USERNAME_RE = /^[A-Za-z0-9._]{1,30}$/;
const IG_ID_RE = /^\d{1,25}$/;

/** Split a free-text list ("@a, b c") into clean tokens. */
export function splitList(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim().replace(/^@/, ""))
    .filter(Boolean);
}

function isHttp(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

/** Clamp to 0..1 and round to 2 decimals — matches the server's normalization. */
function clampVolume(v: number): number {
  return Math.round(Math.min(1, Math.max(0, v)) * 100) / 100;
}

/** The `posts.metadata` keys for the targeted networks (server re-validates). */
export function buildPlatformOptionsMetadata(
  s: PlatformOptionsState,
  ctx: PlatformOptionsContext
): PlatformOptionsMetadata {
  const has = (p: Platform) => ctx.platforms.includes(p);
  const tiktokBranded = has("tiktok") && ctx.tiktokMode === "direct" && s.tiktokDisclose && s.tiktokBrandedContent;
  const out: PlatformOptionsMetadata = {};
  if (s.sponsored || tiktokBranded) out.sponsored = true;

  if (has("facebook") && s.facebookFormat === "story") out.facebook_format = "story";

  if (has("instagram")) {
    if (s.instagramFormat === "story") {
      out.instagram_format = "story";
    } else {
      if (s.instagramTrialReel && ctx.singleVideo) {
        out.instagram_trial_reel = true;
        out.instagram_trial_graduation = s.instagramTrialGraduation;
      } else {
        const collabs = splitList(s.instagramCollaborators).slice(0, IG_COLLABORATORS_MAX);
        if (collabs.length > 0) out.instagram_collaborators = collabs;
      }
    }
    const sponsors = splitList(s.igSponsorIds).slice(0, IG_SPONSOR_IDS_MAX);
    if (s.sponsored && sponsors.length > 0) out.ig_sponsor_ids = sponsors;

    // Instagram audio attach — Reels only (feed format, exactly one video).
    if (
      s.instagramFormat === "feed" &&
      ctx.singleVideo &&
      s.instagramAudio &&
      IG_ID_RE.test(s.instagramAudio.audioId)
    ) {
      const audio: NonNullable<PlatformOptionsMetadata["instagram_audio"]> = {
        audio_id: s.instagramAudio.audioId,
        audio_volume: clampVolume(s.instagramAudio.audioVolume),
        video_volume: clampVolume(s.instagramAudio.videoVolume),
      };
      if (s.instagramAudio.audioType) audio.audio_type = s.instagramAudio.audioType;
      const title = s.instagramAudio.title.trim();
      if (title) audio.title = title.slice(0, 200);
      const artist = s.instagramAudio.artist.trim();
      if (artist) audio.artist = artist.slice(0, 200);
      if (s.instagramAudio.mixOverBakedMusic) audio.mix_over_baked_music = true;
      out.instagram_audio = audio;
    }
  }

  if (has("threads")) {
    if (s.threadsTopicTag.trim()) out.threads_topic_tag = s.threadsTopicTag.trim();
    if (s.threadsReplyControl) out.threads_reply_control = s.threadsReplyControl;
    const options = s.threadsPollOptions.map((o) => o.trim()).filter(Boolean);
    if (s.threadsPollEnabled && options.length >= 2) out.threads_poll_options = options;
  }

  if (has("tiktok") && ctx.tiktokMode === "direct") {
    if (s.tiktokPrivacy) out.tiktok_privacy_level = s.tiktokPrivacy;
    out.tiktok_disable_comment = !s.tiktokAllowComment;
    out.tiktok_disable_duet = !s.tiktokAllowDuet;
    out.tiktok_disable_stitch = !s.tiktokAllowStitch;
    if (s.tiktokDisclose && s.tiktokYourBrand) out.tiktok_brand_organic = true;
    if (tiktokBranded) out.tiktok_brand_content = true;
  }

  if (has("google_business")) {
    if (s.gbpTopicType !== "STANDARD") {
      out.gbp_topic_type = s.gbpTopicType;
      const event: NonNullable<PlatformOptionsMetadata["gbp_event"]> = {
        title: s.gbpEventTitle.trim(),
        start_date: s.gbpStartDate,
        end_date: s.gbpEndDate,
      };
      if (s.gbpStartTime) event.start_time = s.gbpStartTime;
      if (s.gbpEndTime) event.end_time = s.gbpEndTime;
      out.gbp_event = event;
    }
    if (s.gbpTopicType === "OFFER") {
      const offer: NonNullable<PlatformOptionsMetadata["gbp_offer"]> = {};
      if (s.gbpCouponCode.trim()) offer.coupon_code = s.gbpCouponCode.trim();
      if (isHttp(s.gbpRedeemUrl)) offer.redeem_url = s.gbpRedeemUrl.trim();
      if (s.gbpTerms.trim()) offer.terms = s.gbpTerms.trim();
      if (Object.keys(offer).length > 0) out.gbp_offer = offer;
    } else if (s.gbpCtaType) {
      out.gbp_cta_type = s.gbpCtaType;
    }
  }

  if (has("youtube")) {
    if (s.youtubePrivacyStatus) out.youtube_privacy_status = s.youtubePrivacyStatus;
    const title = s.youtubeTitle.trim();
    if (title) out.youtube_title = title.slice(0, YOUTUBE_TITLE_MAX);
    if (s.youtubeMadeForKids) out.youtube_made_for_kids = s.youtubeMadeForKids === "yes";
  }
  return out;
}

/** Problems the server would reject — surfaced in the composer's pre-publish check. */
export function platformOptionsIssues(s: PlatformOptionsState, ctx: PlatformOptionsContext): ComposerIssue[] {
  const issues: ComposerIssue[] = [];
  const has = (p: Platform) => ctx.platforms.includes(p);
  const err = (platform: Platform, code: string, message: string) =>
    issues.push({ platform, severity: "error", code, message });

  if (has("facebook") && s.facebookFormat === "story" && ctx.mediaCount !== 1) {
    err("facebook", "fb_story_media", "A Facebook Story publishes exactly one image or video.");
  }

  if (has("instagram")) {
    if (s.instagramFormat === "story" && ctx.mediaCount !== 1) {
      err("instagram", "ig_story_media", "An Instagram Story publishes exactly one image or video.");
    }
    if (s.instagramFormat === "feed" && s.instagramTrialReel && !ctx.singleVideo) {
      err("instagram", "ig_trial_reel_video", "Trial Reels need exactly one video.");
    }
    const collabs = splitList(s.instagramCollaborators);
    if (s.instagramFormat === "feed" && !s.instagramTrialReel) {
      if (collabs.length > IG_COLLABORATORS_MAX) {
        err("instagram", "ig_collaborators_max", `Invite at most ${IG_COLLABORATORS_MAX} collaborators.`);
      } else if (collabs.some((c) => !IG_USERNAME_RE.test(c))) {
        err("instagram", "ig_collaborators_name", "Collaborators must be Instagram usernames.");
      }
    }
    const sponsors = splitList(s.igSponsorIds);
    if (s.sponsored && (sponsors.length > IG_SPONSOR_IDS_MAX || sponsors.some((i) => !IG_ID_RE.test(i)))) {
      err(
        "instagram",
        "ig_sponsor_ids",
        `Brand partner IDs must be up to ${IG_SPONSOR_IDS_MAX} numeric Instagram IDs.`
      );
    }
    if (s.instagramAudio && (s.instagramFormat === "story" || !ctx.singleVideo)) {
      err(
        "instagram",
        "ig_audio_reels_only",
        "Instagram audio attaches to Reels only (exactly one video, not a Story)."
      );
    }
    if (s.instagramAudio && ctx.bakedMusic === true && !s.instagramAudio.mixOverBakedMusic) {
      err(
        "instagram",
        "ig_audio_baked_music",
        "This video already has music baked in — mix over it anyway, or remove the baked track or the Instagram audio."
      );
    }
  }

  if (has("threads")) {
    const tag = s.threadsTopicTag.trim();
    if (tag.length > THREADS_TOPIC_TAG_MAX) {
      err("threads", "threads_topic_len", `Threads topics can be at most ${THREADS_TOPIC_TAG_MAX} characters.`);
    } else if (/[.&]/.test(tag)) {
      err("threads", "threads_topic_chars", "Threads topics can't contain periods (.) or ampersands (&).");
    }
    if (s.threadsPollEnabled) {
      const options = s.threadsPollOptions.map((o) => o.trim()).filter(Boolean);
      if (options.length < 2) err("threads", "threads_poll_count", "A Threads poll needs at least 2 options.");
      if (options.some((o) => o.length > THREADS_POLL_OPTION_MAX)) {
        err("threads", "threads_poll_len", `Poll options can be at most ${THREADS_POLL_OPTION_MAX} characters.`);
      }
      if (ctx.mediaCount > 0) err("threads", "threads_poll_media", "Threads polls are text-only — remove the media.");
      if (ctx.linkUrl.trim()) err("threads", "threads_poll_link", "Threads polls can't include a link.");
    }
  }

  if (has("tiktok") && ctx.tiktokMode === "direct") {
    if (!s.tiktokPrivacy) err("tiktok", "tiktok_privacy", "Choose who can see this TikTok post.");
    if (s.tiktokDisclose && !s.tiktokYourBrand && !s.tiktokBrandedContent) {
      err("tiktok", "tiktok_disclosure", "Indicate if your content promotes yourself, a third party, or both.");
    }
    const branded = s.sponsored || (s.tiktokDisclose && s.tiktokBrandedContent);
    if (branded && s.tiktokPrivacy === "SELF_ONLY") {
      err("tiktok", "tiktok_branded_private", "Branded content visibility can't be set to private.");
    }
  }

  if (has("google_business")) {
    if (s.gbpTopicType !== "STANDARD") {
      const title = s.gbpEventTitle.trim();
      if (!title || title.length > GBP_EVENT_TITLE_MAX) {
        err("google_business", "gbp_event_title", `Add a title (up to ${GBP_EVENT_TITLE_MAX} characters).`);
      }
      if (!s.gbpStartDate || !s.gbpEndDate) {
        err("google_business", "gbp_event_dates", "Add a start and end date.");
      } else if (`${s.gbpEndDate}T${s.gbpEndTime || "23:59"}` < `${s.gbpStartDate}T${s.gbpStartTime || "00:00"}`) {
        err("google_business", "gbp_event_order", "The end date must be after the start date.");
      }
    }
    if (s.gbpTopicType === "OFFER" && s.gbpRedeemUrl.trim() && !isHttp(s.gbpRedeemUrl)) {
      err("google_business", "gbp_redeem_url", "The redeem link must start with http:// or https://.");
    }
    if (s.gbpTopicType !== "OFFER" && s.gbpCtaType && s.gbpCtaType !== "CALL" && !ctx.linkUrl.trim()) {
      err("google_business", "gbp_cta_link", "Add a link for the Google Business button, or choose Call now.");
    }
  }

  if (has("youtube") && s.youtubeMadeForKids === "") {
    err("youtube", "youtube_made_for_kids", "Choose whether this video is Made for Kids before publishing.");
  }
  if (has("youtube") && s.youtubeTitle.length > YOUTUBE_TITLE_MAX) {
    err("youtube", "youtube_title_len", `YouTube titles can be at most ${YOUTUBE_TITLE_MAX} characters.`);
  }
  return issues;
}

/** TikTok's required label prompt for the chosen disclosure (content-sharing guidelines). */
export function tiktokDisclosureLabel(s: PlatformOptionsState): string | null {
  if (!s.tiktokDisclose) return null;
  if (s.tiktokBrandedContent) return "Your video will be labeled as 'Paid partnership'.";
  if (s.tiktokYourBrand) return "Your video will be labeled as 'Promotional content'.";
  return null;
}

/** TikTok's required consent declaration shown before the publish button. */
export function tiktokDeclaration(s: PlatformOptionsState): string {
  return s.tiktokDisclose && s.tiktokBrandedContent
    ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
    : "By posting, you agree to TikTok's Music Usage Confirmation.";
}
